/**
 * "What's new" state helpers — pure functions + a guarded localStorage
 * accessor so the AppShell button and drawer share one source of truth.
 *
 * Storage key `whatsNewSeen` holds the newest release date (`YYYY-MM-DD`)
 * the user has opened the panel at. Unread = newest entry date is strictly
 * newer than the stored date. When storage is unavailable (private mode,
 * blocked site data) we return `null` and the caller hides the dot entirely
 * rather than nagging on every load.
 */
import changelog from '@/generated/changelog.json';
import { type ChangelogEntry, newestChangelogDate } from '@/lib/changelog';

export const WHATS_NEW_STORAGE_KEY = 'whatsNewSeen';

export const CHANGELOG_ENTRIES: ChangelogEntry[] = changelog.entries as ChangelogEntry[];

/** Returns the stored date, `null` when nothing stored, or `undefined` when storage is unusable. */
export function readWhatsNewSeen(): string | null | undefined {
  try {
    return window.localStorage.getItem(WHATS_NEW_STORAGE_KEY);
  } catch {
    return undefined;
  }
}

/** Returns false when storage is unusable so callers can hide the dot. */
export function writeWhatsNewSeen(date: string): boolean {
  try {
    window.localStorage.setItem(WHATS_NEW_STORAGE_KEY, date);
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether the unread dot should show.
 *   - no entries → false
 *   - storage unusable → false (hidden, per spec)
 *   - nothing stored yet → true (first visit sees the dot)
 *   - stored < newest → true
 */
export function hasUnreadChangelog(
  entries: ReadonlyArray<ChangelogEntry>,
  seen: string | null | undefined,
): boolean {
  const newest = newestChangelogDate(entries);
  if (newest === null) return false;
  if (seen === undefined) return false;
  if (seen === null) return true;
  return seen < newest;
}
